import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, ArrowRight, Plane } from "lucide-react";
import { api, apiError, eur } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Checkout() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const from = params.get("from");
  const to = params.get("to");
  const classId = params.get("class");
  const pax = Number(params.get("pax") || 2);
  const rt = params.get("rt") === "1";
  const date = params.get("date") || new Date().toISOString().slice(0, 10);
  const time = params.get("time") || "10:00";
  const ret = params.get("ret") || "";

  const [quote, setQuote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flightInfo, setFlightInfo] = useState(null);
  const [checkingFlight, setCheckingFlight] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    email: "",
    flight_number: "",
    luggage: pax,
    notes: "",
  });

  useEffect(() => {
    if (!from || !to || !classId) {
      navigate("/");
      return;
    }
    api
      .post("/quote", { origin_id: from, destination_id: to, pax, round_trip: rt })
      .then(({ data }) => setQuote(data))
      .catch((e) => toast.error(apiError(e)));
  }, [from, to, classId, pax, rt, navigate]);

  useEffect(() => {
    if (user)
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        phone: f.phone || user.phone || "",
        email: f.email || user.email || "",
      }));
  }, [user]);

  const option = quote?.options?.find((o) => o.class_id === classId);

  const checkFlight = async () => {
    const fn = form.flight_number.trim();
    if (!fn) return;
    if (!user) {
      toast.error(t("checkout.loginHint"));
      return;
    }
    setCheckingFlight(true);
    setFlightInfo(null);
    try {
      const { data } = await api.get(`/flights/${encodeURIComponent(fn)}`, { params: { date } });
      setFlightInfo(data);
    } catch (e) {
      toast.error(apiError(e));
    }
    setCheckingFlight(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error(t("checkout.loginHint"));
      navigate("/login", { state: { next: `/checkout?${params.toString()}` } });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/bookings", {
        origin_id: from,
        destination_id: to,
        vehicle_class_id: classId,
        pax,
        luggage: Number(form.luggage) || 0,
        pickup_at: `${date}T${time}:00`,
        return_pickup_at: rt && ret ? `${ret}:00`.slice(0, 19) : null,
        round_trip: rt,
        flight_number: form.flight_number || null,
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email,
        notes: form.notes || null,
        payment_method: "cash",
        language: lang,
      });
      toast.success(t("confirmed.title"));
      navigate(`/confirmation/${data.code}`, { state: { booking: data } });
    } catch (err) {
      toast.error(apiError(err));
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-6xl px-5 py-12 lg:px-10 lg:py-16">
        <h1 className="font-display text-3xl tracking-tight sm:text-4xl" data-testid="checkout-title">
          {t("checkout.title")}
        </h1>

        <div className="mt-10 grid gap-10 md:grid-cols-12">
          <form onSubmit={submit} className="md:col-span-7" data-testid="checkout-form">
            <div className="grid gap-5 rounded-xl border border-slate-200 bg-white p-6 ambient sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="name">{t("checkout.name")}</Label>
                <Input
                  id="name"
                  data-testid="checkout-name-input"
                  required
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="phone">{t("checkout.phone")}</Label>
                <Input
                  id="phone"
                  data-testid="checkout-phone-input"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="email">{t("checkout.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="checkout-email-input"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="flight">{t("checkout.flight")}</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    id="flight"
                    data-testid="checkout-flight-input"
                    placeholder="TK2401"
                    value={form.flight_number}
                    onChange={(e) => { setForm({ ...form, flight_number: e.target.value.toUpperCase() }); setFlightInfo(null); }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="check-flight-btn"
                    disabled={!form.flight_number.trim() || checkingFlight}
                    onClick={checkFlight}
                    className="shrink-0"
                  >
                    <Plane size={14} className="mr-1.5" /> {checkingFlight ? t("common.loading") : t("ops.checkFlight")}
                  </Button>
                </div>
                {flightInfo && (
                  <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="flight-info">
                    <p className="font-semibold text-slate-800">
                      {flightInfo.flight_number} · {flightInfo.airport_iata}
                      <span className={`ml-2 rounded-full px-2 py-0.5 font-medium ${flightInfo.delayed ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {flightInfo.delayed ? t("ops.delayed") : (flightInfo.status || t("ops.onTime"))}
                      </span>
                    </p>
                    <p className="mt-1.5 text-slate-600">{t("ops.scheduledArr")}: {flightInfo.scheduled_arrival || "—"}</p>
                    {flightInfo.estimated_arrival && <p className="text-slate-600">{t("ops.estimatedArr")}: {flightInfo.estimated_arrival}</p>}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="luggage">{t("results.bags")}</Label>
                <Input
                  id="luggage"
                  type="number"
                  min={0}
                  max={40}
                  data-testid="checkout-luggage-input"
                  value={form.luggage}
                  onChange={(e) => setForm({ ...form, luggage: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="notes">{t("checkout.notes")}</Label>
                <Textarea
                  id="notes"
                  data-testid="checkout-notes-input"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1.5"
                />
              </div>

              <div className="sm:col-span-2 rounded-lg border border-gold/30 bg-gold/5 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-gold-dark">
                  <Wallet size={16} /> {t("checkout.cash")}
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{t("checkout.cashNote")}</p>
              </div>

              {!user && (
                <p className="sm:col-span-2 text-sm text-slate-600">
                  {t("checkout.loginHint")}{" "}
                  <Link to="/login" className="font-semibold text-sea underline">
                    {t("auth.signin")}
                  </Link>
                </p>
              )}

              <Button
                data-testid="confirm-booking-btn"
                type="submit"
                disabled={saving || !option}
                className="sm:col-span-2 h-12 bg-gold text-base font-semibold hover:bg-gold-dark"
              >
                {saving ? t("common.loading") : t("checkout.confirm")}
              </Button>
            </div>
          </form>

          <aside className="md:col-span-5">
            <div className="rounded-xl border border-slate-200 bg-white p-6 ambient" data-testid="trip-summary">
              <p className="label-eyebrow">{t("checkout.summary")}</p>
              {!quote && <p className="mt-4 text-sm text-slate-500">{t("common.loading")}</p>}
              {quote && (
                <>
                  <p className="mt-4 text-sm font-medium text-slate-900">
                    {quote.origin.name} <ArrowRight size={13} className="inline text-slate-400" />{" "}
                    {quote.destination.name}
                  </p>
                  <dl className="mt-5 space-y-3 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-500">{t("checkout.vehicle")}</dt>
                      <dd className="font-medium">{option?.name || "—"}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">{t("checkout.pickup")}</dt>
                      <dd className="font-medium">
                        {date} · {time}
                      </dd>
                    </div>
                    {rt && ret && (
                      <div className="flex justify-between">
                        <dt className="text-slate-500">{t("search.ret")}</dt>
                        <dd className="font-medium">{ret.replace("T", " · ")}</dd>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <dt className="text-slate-500">{t("checkout.guests")}</dt>
                      <dd className="font-medium">{pax}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">{t("results.route")}</dt>
                      <dd className="font-medium">
                        {quote.distance_km} {t("results.km")} · {quote.duration_min} {t("results.min")}
                      </dd>
                    </div>
                  </dl>
                  <div className="mt-6 flex items-end justify-between border-t border-slate-200 pt-5">
                    <span className="text-sm text-slate-500">{t("checkout.price")}</span>
                    <span className="font-display text-3xl text-sea" data-testid="summary-total">
                      {eur(option?.price)}
                    </span>
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
